import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import api from "../lib/api";

export default function WeekEntry() {
  const { leagueId, weekId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!weekId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [league, setLeague] = useState(null);
  const [game, setGame] = useState(null);
  const [weekNumber, setWeekNumber] = useState(1);
  // Use local date format to avoid timezone issues
  const [date, setDate] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [playerStats, setPlayerStats] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const leagueRes = await api.getLeague(leagueId);
        setLeague(leagueRes.data);

        const gameRes = await api.getGame(leagueRes.data.game_id);
        setGame(gameRes.data);

        // Initialize player stats
        const initialStats = leagueRes.data.players.map((player) => ({
          user_id: player.user_id,
          username: player.username,
          stats: gameRes.data.fields.reduce((acc, field) => {
            acc[field.name] = field.default_value;
            return acc;
          }, {}),
        }));

        if (isEditing) {
          const weekRes = await api.getWeek(weekId);
          setWeekNumber(weekRes.data.week_number);
          setDate(weekRes.data.date);
          setPlayerStats(weekRes.data.player_stats);
        } else {
          setPlayerStats(initialStats);
          // Get next week number
          if (leagueRes.data.weeks?.length > 0) {
            const maxWeek = Math.max(...leagueRes.data.weeks.map((w) => w.week_number));
            setWeekNumber(maxWeek + 1);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Veri yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [leagueId, weekId, isEditing]);

  const updatePlayerStat = (playerIndex, fieldName, value) => {
    setPlayerStats((prev) => {
      const updated = [...prev];
      updated[playerIndex] = {
        ...updated[playerIndex],
        stats: {
          ...updated[playerIndex].stats,
          [fieldName]: value,
        },
      };
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        league_id: leagueId,
        week_number: weekNumber,
        date: date,
        player_stats: playerStats,
      };

      if (isEditing) {
        await api.updateWeek(weekId, data);
        toast.success("Hafta güncellendi!");
      } else {
        await api.createWeek(data);
        toast.success("Hafta kaydedildi!");
      }
      navigate(`/league/${leagueId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bir hata oluştu");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#4DFFFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="week-entry-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link to={`/league/${leagueId}`}>
          <Button variant="ghost" size="icon" className="text-[#B1C1C1] hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
            {isEditing ? `Hafta ${weekNumber} Düzenle` : "Yeni Hafta"}
          </h1>
          <p className="text-[#B1C1C1] text-sm mt-1">
            {league?.name} • {game?.name_tr || game?.name}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Week Info */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#C6A87C] mb-4">
            Hafta Bilgileri
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="weekNumber" className="text-[#B1C1C1]">
                Hafta Numarası
              </Label>
              <Input
                id="weekNumber"
                type="number"
                min="1"
                value={weekNumber}
                onChange={(e) => setWeekNumber(parseInt(e.target.value) || 1)}
                className="mt-2 bg-[#050E2E]/50 border-white/10 text-white h-12"
              />
            </div>
            <div>
              <Label htmlFor="date" className="text-[#B1C1C1]">
                Tarih
              </Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-2 justify-start text-left font-normal bg-[#050E2E]/50 border-white/10 text-white h-12 hover:bg-[#050E2E]/70"
                    data-testid="date-picker-trigger"
                    onClick={() => setCalendarOpen(true)}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#64748B]" />
                    {date ? format(new Date(date), "d MMMM yyyy", { locale: tr }) : "Tarih seçin"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-auto p-0 bg-[#0A1635] border-white/10" 
                  align="start" 
                  sideOffset={5}
                  onInteractOutside={(e) => {
                    e.preventDefault();
                  }}
                >
                  <Calendar
                    mode="single"
                    selected={date ? new Date(date + "T12:00:00") : undefined}
                    onSelect={(selectedDate) => {
                      if (selectedDate) {
                        // Use local date to avoid timezone issues
                        const year = selectedDate.getFullYear();
                        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
                        const day = String(selectedDate.getDate()).padStart(2, '0');
                        setDate(`${year}-${month}-${day}`);
                        setCalendarOpen(false);
                      }
                    }}
                    locale={tr}
                    defaultMonth={date ? new Date(date + "T12:00:00") : new Date()}
                    className="text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        {/* Player Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {playerStats.map((player, playerIndex) => (
            <div
              key={player.user_id}
              className="glass-card overflow-hidden"
              data-testid={`player-card-${player.username}`}
            >
              {/* Player Header */}
              <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-6 py-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4DFFFF]/20 to-[#4DFFFF]/5 flex items-center justify-center border border-[#4DFFFF]/30">
                    <span className="text-xl font-bold text-[#4DFFFF]">
                      {player.username?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                      {league?.players?.find((p) => p.user_id === player.user_id)?.name ||
                        player.username}
                    </h3>
                    <p className="text-sm text-[#64748B]">@{player.username}</p>
                  </div>
                </div>
              </div>

              {/* Fields */}
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  {game?.fields?.map((field) => (
                    <div key={field.name}>
                      <Label className="text-xs text-[#64748B]">{field.label}</Label>
                      {field.field_type === "boolean" ? (
                        <div className="mt-2 flex items-center gap-2">
                          <Checkbox
                            checked={player.stats[field.name] || false}
                            onCheckedChange={(checked) =>
                              updatePlayerStat(playerIndex, field.name, checked)
                            }
                          />
                          <span className="text-sm text-white">
                            {player.stats[field.name] ? "Evet" : "Hayır"}
                          </span>
                        </div>
                      ) : (
                        <Input
                          type="number"
                          min="0"
                          value={player.stats[field.name] ?? 0}
                          onChange={(e) =>
                            updatePlayerStat(
                              playerIndex,
                              field.name,
                              e.target.value === "" ? 0 : parseInt(e.target.value) || 0
                            )
                          }
                          onFocus={(e) => {
                            if (e.target.value === "0") {
                              e.target.select();
                            }
                          }}
                          className="mt-1 bg-[#050E2E]/50 border-white/10 text-white h-11"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Submit */}
        <div className="mt-8 flex justify-end gap-4">
          <Link to={`/league/${leagueId}`}>
            <Button variant="ghost" className="text-[#B1C1C1] hover:text-white">
              İptal
            </Button>
          </Link>
          <Button
            type="submit"
            disabled={saving}
            className="bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold px-8"
            data-testid="save-week-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? "Güncelle" : "Kaydet"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
